import { computed } from 'vue';

import {
  useAccessChecklistViewModel,
  useAccessControlStore,
  useActiveIdentity,
  type AccessChecklistItem,
  type AccessRequirementKey,
} from '@peanuds/app-shell';
import { storeToRefs } from 'pinia';

const LOCAL_DIALOG_CTA_LABELS: Partial<Record<AccessRequirementKey, string>> = {
  email: 'Verify Email',
  phone: 'Verify phone number',
};

export const useTotalBalanceAccessViewModel = () => {
  const accessControlStore = useAccessControlStore();
  const { isOnboarded, requirements } = storeToRefs(accessControlStore);
  const { hasEmailConfirmationClaim, isEmailConfirmed } = useActiveIdentity();
  const { currentItem, runRequirementCta } = useAccessChecklistViewModel();
  const isEmailVerificationAvailable = computed(() => {
    return hasEmailConfirmationClaim.value && !isEmailConfirmed.value;
  });

  const currentOnboardingItem = computed<AccessChecklistItem | null>(() => {
    if (!currentItem.value) {
      return null;
    }

    const localCtaLabel = LOCAL_DIALOG_CTA_LABELS[currentItem.value.key];

    if (
      !localCtaLabel ||
      (currentItem.value.key === 'email' && !isEmailVerificationAvailable.value)
    ) {
      return currentItem.value;
    }

    return {
      ...currentItem.value,
      ctaLabel: localCtaLabel,
      ctaVisible: true,
      ctaDisabled: false,
      ctaKind: 'start',
    };
  });

  const showOnboardingContent = computed(() => {
    return !isOnboarded.value && currentOnboardingItem.value !== null;
  });

  const isVerificationLoading = computed(() => {
    const currentItem = currentOnboardingItem.value;

    if (
      !currentItem ||
      (currentItem.key !== 'kyc' && currentItem.key !== 'kyb') ||
      currentItem.status !== 'NOT_STARTED'
    ) {
      return false;
    }

    return (
      requirements.value.find((requirement) => requirement.key === currentItem.key)
        ?.sourceStatus === 'loading'
    );
  });

  const handleRequirementCta = async (key: AccessRequirementKey) => {
    const item = currentOnboardingItem.value;

    if (item?.key !== key || !item.ctaVisible || item.ctaDisabled) {
      return;
    }

    if (LOCAL_DIALOG_CTA_LABELS[key]) {
      return;
    }

    await runRequirementCta(key);
  };

  return {
    currentOnboardingItem,
    showOnboardingContent,
    isVerificationLoading,
    isOnboarded,
    isEmailVerificationAvailable,
    handleRequirementCta,
  };
};
